import {useCallback, useEffect, useState} from 'react';
import useWindowViewportWidth from "../viewport-width";
import {deepEqual} from "../../../../core/services/utils/utils";

/**
 * Fetches the current viewport width and height of the window
 * @return {{width: number, height: number, viewportWidth: viewportWidthOption}}
 */
const getSize = (viewportWidth) => {
    const width = Math.max(document.documentElement.clientWidth, (window.innerWidth || 0))
    const height = Math.max(document.documentElement.clientHeight, (window.innerHeight || 0))
    return {
        width: width,
        height: height,
        viewportWidth: viewportWidth,
    };
}

export default function useWindowSize() {
    const isClient = typeof window === 'object';
    const viewportWidth = useWindowViewportWidth();
    const [windowSize, setWindowSize] = useState(getSize(viewportWidth));

    /**
     * Sets the current window size in the state to be returned from this hook.
     * @type {(function(): void)|*}
     */
    const onWindowResize = useCallback(() => {
        const newWindowSize = getSize(viewportWidth);
        if (!deepEqual(newWindowSize, windowSize))
            setWindowSize(newWindowSize);
    }, [viewportWidth, windowSize])

    /**
     * Listens to the changes in onWindowResize and whether the window is instantiated and with each change:
     * Sets the event listener on the resizing of window for the hook to be effective.
     */
    useEffect(() => {
        if (!isClient)
            return;
        window.addEventListener('resize', onWindowResize);
        onWindowResize();
        return () => window.removeEventListener('resize', onWindowResize);
    }, [isClient, onWindowResize]);

    return windowSize;
}
